import React, { useEffect, useState } from "react";
import { Plus, Pencil, X, Shield } from "lucide-react";
import dayjs from "dayjs";
import { toast } from "../../../ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

// ---------- Reusable CRUD panel ----------
function CrudPanel({ title, fields, apiPath, renderRow, emptyMsg }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft,   setDraft]   = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const token = localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}${apiPath}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(null);
    const isNew = editing === "new";
    try {
      const res = await fetch(isNew ? `${API_BASE}${apiPath}` : `${API_BASE}${apiPath}/${draft.id}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load(); setEditing(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      const res = await fetch(`${API_BASE}${apiPath}/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems(i => i.filter(x => x.id !== id));
      toast.success("Deleted successfully");
    } catch (e) {
      setError(e.message);
      toast.error("Failed to delete");
    }
  };

  const empty = fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? "" }), {});

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">{title}</span>
        <button onClick={() => { setDraft(empty); setEditing("new"); }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> Add
        </button>
      </div>

      {editing && (
        <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key} className={f.span2 ? "col-span-2" : ""}>
                <label className={labelCls}>{f.label}</label>
                {f.type === "select" ? (
                  <select className={inputCls} value={draft[f.key] ?? ""} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}>
                    {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input type="checkbox" checked={!!draft[f.key]} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.checked }))} className="w-4 h-4 rounded accent-[#00ff90]" />
                    <span className="text-xs text-white/50">{f.checkLabel || f.label}</span>
                  </label>
                ) : (
                  <input type={f.type || "text"} step={f.step} className={inputCls} value={draft[f.key] ?? ""} placeholder={f.placeholder || ""}
                    onChange={e => setDraft(d => ({ ...d, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))} />
                )}
                {f.help && <p className="text-[11px] text-white/30 mt-1 leading-snug">{f.help}</p>}
              </div>
            ))}
          </div>
          {error && <p className="text-red-400 text-xs">⚠ {error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>)
        ) : items.length === 0 ? (
          <p className="px-5 py-6 text-white/20 text-sm italic text-center">{emptyMsg}</p>
        ) : items.map(item => (
          <div key={item.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex-1 min-w-0">{renderRow(item)}</div>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => { setDraft({ ...item }); setEditing(item.id); }}
                className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                <Pencil size={12} />
              </button>
              <button onClick={() => del(item.id)}
                className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                <X size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Rules Engine ----------
function RulesEngine() {
  const [rules,   setRules]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft,   setDraft]   = useState({});
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const token = localStorage.getItem("token");

  const RULE_TYPES = [
    "min_notice_days", "max_consecutive_days", "min_team_coverage_pct",
    "probation_restriction_days", "tenure_tier_entitlement", "auto_approve_threshold",
    "max_leave_per_year", "require_doc_after_days",
  ];

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/rules`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRules(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(null);
    const isNew = editing === "new";
    try {
      const res = await fetch(isNew ? `${API_BASE}/api/admin/leave-rules/rules` : `${API_BASE}/api/admin/leave-rules/rules/${draft.id}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load(); setEditing(null);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/rules/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRules(r => r.filter(x => x.id !== id));
      toast.success("Deleted successfully");
    } catch (e) {
      setError(e.message);
      toast.error("Failed to delete");
    }
  };

  const RULE_LABEL = {
    min_notice_days:           "Min Notice Period",
    max_consecutive_days:      "Max Consecutive Days",
    min_team_coverage_pct:     "Min Team Coverage %",
    probation_restriction_days:"Probation Restriction",
    tenure_tier_entitlement:   "Tenure Tier Entitlement",
    auto_approve_threshold:    "Auto-Approve Threshold",
    max_leave_per_year:        "Max Leave / Year",
    require_doc_after_days:    "Doc Required After (days)",
  };

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Policy Rules Engine</span>
        </div>
        <button onClick={() => { setDraft({ rule_type: "min_notice_days", leave_type: "ALL", scope: "Global", value: 1, enabled: true }); setEditing("new"); }}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> New Rule
        </button>
      </div>

      {editing && (
        <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Rule Type</label>
              <select className={inputCls} value={draft.rule_type || ""} onChange={e => setDraft(d => ({ ...d, rule_type: e.target.value }))}>
                {RULE_TYPES.map(r => <option key={r} value={r}>{RULE_LABEL[r] || r}</option>)}
              </select></div>
            <div><label className={labelCls}>Leave Type</label>
              <select className={inputCls} value={draft.leave_type || "ALL"} onChange={e => setDraft(d => ({ ...d, leave_type: e.target.value }))}>
                {["ALL","Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Scope</label>
              <input className={inputCls} value={draft.scope || ""} onChange={e => setDraft(d => ({ ...d, scope: e.target.value }))} placeholder="Global / Engineering / London…" /></div>
            <div><label className={labelCls}>Value</label>
              <input type="number" step="1" className={inputCls} value={draft.value ?? ""} onChange={e => setDraft(d => ({ ...d, value: parseFloat(e.target.value) }))} /></div>
            <div className="col-span-2"><label className={labelCls}>Description (optional)</label>
              <input className={inputCls} value={draft.description || ""} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="e.g. Annual leave requires 5 days notice" /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!draft.enabled} onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))} className="w-4 h-4 rounded accent-[#00ff90]" />
            <span className="text-xs text-white/50">Rule enabled</span>
          </label>
          {error && <p className="text-red-400 text-xs">⚠ {error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={save} disabled={saving}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/5 max-h-72 overflow-y-auto">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-8 rounded-lg bg-white/5 animate-pulse" /></div>)
        ) : rules.length === 0 ? (
          <p className="px-5 py-6 text-white/20 text-sm italic text-center">No rules configured.</p>
        ) : rules.map(r => (
          <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm text-white/70">{RULE_LABEL[r.rule_type] || r.rule_type}</span>
                <span className="text-xs text-white/30">= {r.value}</span>
                {!r.enabled && <span className="text-xs text-white/20 border border-white/10 px-1.5 py-0.5 rounded-full">Disabled</span>}
              </div>
              <p className="text-xs text-white/25">
                {r.leave_type !== "ALL" ? r.leave_type : "All types"} · {r.scope}
                {r.description ? ` · ${r.description}` : ""}
              </p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => { setDraft({ ...r }); setEditing(r.id); }}
                className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                <Pencil size={12} />
              </button>
              <button onClick={() => del(r.id)}
                className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                <X size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Main PolicyTab ----------
export default function PolicyTab() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <CrudPanel
          title="Leave Types"
          apiPath="/api/admin/leave-rules/types"
          emptyMsg="No leave types configured."
          fields={[
            { key: "name",         label: "Name",               placeholder: "Annual / Sick…" },
            { key: "max_days",     label: "Max Days / Year",    type: "number" },
            { key: "carry_cap",    label: "Carry-over Cap",     type: "number", default: 0 },
            { key: "paid",         label: "Paid",               type: "checkbox", checkLabel: "This is a paid leave type", default: true },
            { key: "negative",     label: "Allow Negative",     type: "checkbox", checkLabel: "Allow negative balance" },
            { key: "requires_doc", label: "Requires Document",  type: "checkbox", checkLabel: "Proof required" },
            { key: "tone",         label: "Return tone",        type: "select", opts: ["Standard", "Sensitive"], default: "Standard", span2: true,
              help: "Sensitive softens KIRA's welcome when someone returns from this leave (e.g. bereavement, compassionate)." },
          ]}
          renderRow={t => (
            <>
              <p className="text-sm text-white/70 font-medium">{t.name}</p>
              <p className="text-xs text-white/25 mt-0.5">
                Carry: {t.carry_cap}d{t.max_days ? ` · Max: ${t.max_days}d/yr` : ""}
                {t.paid ? " · Paid" : " · Unpaid"}
                {t.negative ? " · Neg. bal" : ""}
                {t.requires_doc ? " · Doc req." : ""}
                {String(t.tone).toLowerCase() === "sensitive" ? " · Sensitive" : ""}
              </p>
            </>
          )}
        />

        <CrudPanel
          title="Accrual Rules"
          apiPath="/api/admin/leave-rules/accruals"
          emptyMsg="No accrual rules configured."
          fields={[
            { key: "scope",             label: "Scope",            placeholder: "Global / Dept / Role" },
            { key: "leave_type",        label: "Leave Type",       placeholder: "Annual / Sick…" },
            { key: "frequency",         label: "Frequency",        type: "select", opts: ["Monthly","Quarterly","Annually","Bi-monthly"], default: "Monthly" },
            { key: "per_period",        label: "Days per Period",   type: "number", step: 0.5, default: 1.5 },
            { key: "start_after_days",  label: "Start After (days)",type: "number", default: 30 },
            { key: "carry_expiry",      label: "Carry Expiry",     placeholder: "e.g. Mar 31" },
          ]}
          renderRow={r => (
            <>
              <p className="text-sm text-white/70 font-medium">{r.scope} {r.leave_type && `· ${r.leave_type}`}</p>
              <p className="text-xs text-white/25 mt-0.5">{r.frequency} · {r.per_period}/period · Start+{r.start_after_days}d{r.carry_expiry ? ` · Exp: ${r.carry_expiry}` : ""}</p>
            </>
          )}
        />

        <CrudPanel
          title="Blackout Periods"
          apiPath="/api/admin/leave-rules/blackouts"
          emptyMsg="No blackout periods set."
          fields={[
            { key: "scope",  label: "Scope",  placeholder: "Global / Dept / Role" },
            { key: "reason", label: "Reason", placeholder: "Year-end freeze…" },
            { key: "from",   label: "From",   type: "date" },
            { key: "to",     label: "To",     type: "date" },
          ]}
          renderRow={b => (
            <>
              <p className="text-sm text-white/70 font-medium">{b.scope}</p>
              <p className="text-xs text-white/25 mt-0.5">
                {b.from && dayjs(b.from).format("DD MMM YYYY")} → {b.to && dayjs(b.to).format("DD MMM YYYY")}
                {b.reason ? ` · ${b.reason}` : ""}
              </p>
            </>
          )}
        />

        <CrudPanel
          title="Holiday Calendar"
          apiPath="/api/admin/leave-rules/holidays"
          emptyMsg="No holidays configured."
          fields={[
            { key: "name",     label: "Name",           placeholder: "e.g. Founders Day" },
            { key: "date",     label: "Date",           type: "date" },
            { key: "location", label: "Location/Scope", placeholder: "Global / London…", default: "Global" },
            { key: "optional", label: "Optional",       type: "checkbox", checkLabel: "Floating / optional holiday" },
          ]}
          renderRow={h => (
            <>
              <p className="text-sm text-white/70 font-medium">{h.name}</p>
              <p className="text-xs text-white/25 mt-0.5">
                {h.date && dayjs(h.date).format("DD MMM YYYY")} · {h.location}
                {h.optional ? " · Optional" : ""}
              </p>
            </>
          )}
        />
      </div>

      <RulesEngine />
    </div>
  );
}